import type {WorkflowResult} from '@linx-security/api-specs';
import {WorkflowResultType} from '@linx-security/api-specs';
import type {ApiResponse} from '@linx-security/api';

import type {createBackgroundTasksStore} from './store';

export type TaskToastRetryButton = {
  children?: string;
  onClick?: () => void;
  href?: string;
  [key: string]: unknown;
};

export type TaskToastRetry = ((status: string) => TaskToastRetryButton | null) | TaskToastRetryButton | null;

export type TaskStatus = WorkflowResultType;
export const TaskStatus = WorkflowResultType;
// Rename for this type to not be confused with external types
export type BackgroundTaskResult<Type extends TaskStatus = TaskStatus> = Omit<WorkflowResult, 'type'> & {type: Type};

export type Task = BackgroundTaskResult & {
  isFetching?: boolean;
  isNotified?: boolean;
  /** When true, the global notification hook will skip toasts for this task. Use when the consumer handles its own notifications. */
  silent?: boolean;
  interval?: number;
  /**
   * Set when this task's export was issued by a service other than the default (api_service) —
   * subscribe polling routes to that service's own background_tasks endpoint instead. Must live
   * on the task itself (not read from a live feature flag) because tasks are persisted across
   * navigation and the flag may have changed by the time polling resumes.
   */
  subscribeTarget?: 'query-service' | 'user-management-service';
  toast?: {
    id?: string;
    success?: string;
    error?: string;
    loading?: string;
    retry?: TaskToastRetry;
  };
};

export type SuccessfulDownloadResult = BackgroundTaskResult<'SUCCESS'> & {
  payload: {
    url: string;
  };
};

export type SubscribeFn = (
  taskId: string,
  target?: Task['subscribeTarget'],
) => Promise<ApiResponse<{id: string; result: WorkflowResult}>>;

export type Dependencies = {
  /**
   * Subscribes to a background task, routing to query_service instead of the default
   * (api_service) when called with `target: 'query-service'`.
   */
  subscribe: SubscribeFn;
};

export type State = {
  tasks: Map<string, Task>;
};

export type Actions = {
  // External actions
  updateTask: (id: string, task: Task) => void;
  addTask: (id: string, task: Omit<Task, 'isNotified' | 'isFetching'>) => Task;
  removeTask: (id: string) => void;
  // Internal actions
  _onTaskSuccess: (taskId: string, payload?: Partial<BackgroundTaskResult<'SUCCESS'>> & Task) => void;
  _onTaskFailure: (taskId: string, payload?: Partial<BackgroundTaskResult<'FAILURE'>> & Task) => void;
  _onTaskPending: (taskId: string, payload?: Partial<BackgroundTaskResult<'PENDING'>> & Omit<Task, 'type'>) => void;
};

export type BackgroundTasksStore = State & Actions;
export type BackgroundTasksStoreApi = ReturnType<typeof createBackgroundTasksStore>;
